import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from '../../hooks/use-toast';
import axios from 'axios';
import { emit, EVT, emitMany } from '../../services/dataSyncBus';
import { track, trackFirstTime } from '../../services/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AddTenantModal = ({ isOpen, onClose, onSuccess, token, bookingId, prefill }) => {
  const [properties, setProperties] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [selectedRoomInfo, setSelectedRoomInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    property_id: '',
    room_id: '',
    name: '',
    phone: '',
    email: '',
    kyc_type: '',
    kyc_number: '',
    check_in_date: new Date().toISOString().split('T')[0],
    rent_amount: '',
    deposit_amount: '',
    emergency_contact_name: '',
    emergency_contact_number: '',
    // Move-in only: where to credit the booking's token payment
    token_adjust_to: 'deposit',
  });

  // If launched from a booking's "Move In" flow, pre-populate the form
  useEffect(() => {
    if (isOpen && prefill) {
      setFormData(prev => ({ ...prev, ...prefill }));
    }
  }, [isOpen, prefill]);

  const fetchProperties = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/properties`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProperties(response.data);
    } catch (error) {
      console.error('Error fetching properties:', error);
    }
  }, [token]);

  const fetchRooms = useCallback(async (propertyId) => {
    try {
      const response = await axios.get(`${API}/properties/${propertyId}/rooms`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRooms(response.data);
    } catch (error) {
      console.error('Error fetching rooms:', error);
      setRooms([]);
    }
  }, [token]);

  useEffect(() => {
    if (isOpen) {
      fetchProperties();
    }
  }, [isOpen, fetchProperties]);

  useEffect(() => {
    if (formData.property_id) {
      fetchRooms(formData.property_id);
    }
  }, [formData.property_id, fetchRooms]);

  const handleRoomChange = (roomId) => {
    setFormData({...formData, room_id: roomId, rent_amount: '', deposit_amount: ''});
    
    // Find selected room info
    const room = rooms.find(r => r.id === roomId);
    
    if (room) {
      setSelectedRoomInfo(room);
      
      // Smart default: If 1-bed room with existing tenants, auto-fill rent as 0
      // (User can change it if needed)
      if (room.total_beds === 1 && room.tenants && room.tenants.length > 0) {
        setFormData({
          ...formData, 
          room_id: roomId, 
          rent_amount: '0',  // Auto-fill 0 for 2nd tenant in shared room
          deposit_amount: ''
        });
      } else {
        setFormData({...formData, room_id: roomId, rent_amount: '', deposit_amount: ''});
      }
    } else {
      setSelectedRoomInfo(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Convert empty strings to 0 for numeric fields
      const submitData = {
        ...formData,
        rent_amount: formData.rent_amount === '' ? 0 : parseFloat(formData.rent_amount),
        deposit_amount: formData.deposit_amount === '' ? 0 : parseFloat(formData.deposit_amount)
      };
      
      // Route: booking-move-in endpoint if launched from a booking, else normal tenant create
      if (bookingId) {
        const res = await axios.post(`${API}/bookings/${bookingId}/move-in`, submitData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        // Move-in writes tenant + (optional) payment + booking status all at once
        emitMany([EVT.TENANT, EVT.BOOKING, EVT.ROOM, EVT.PAYMENT, EVT.LEAD], { action: 'move_in', booking_id: bookingId });
        track('tenant_moved_in', {
          booking_id: bookingId,
          token_adjust_to: submitData.token_adjust_to,
          token_payment_logged: !!res.data?.token_payment_logged,
        });
        toast({
          title: "Tenant Moved In!",
          description: (() => {
            // Auto-filled message so the owner gets a clear summary of what
            // happened with the token at move-in time, without typing anything.
            const tokenAmt = Number(res.data?.token_amount || 0);
            const adjustedTo = res.data?.token_adjusted_to; // 'rent' | 'deposit' | null
            if (!res.data?.token_payment_logged || !tokenAmt) {
              return "Tenant created from booking.";
            }
            const target = adjustedTo === 'rent' ? "first month's rent" : "security deposit";
            const amtStr = `₹${tokenAmt.toLocaleString('en-IN')}`;
            return `Tenant created · ${amtStr} token credited to ${target}.`;
          })(),
        });
      } else {
        await axios.post(`${API}/tenants`, submitData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        emit(EVT.TENANT, { action: 'created' });
        emit(EVT.ROOM,   { action: 'tenant_added' });
        track('tenant_created', { surface: 'add_tenant_modal' });
        trackFirstTime('first_tenant_added');
        toast({
          title: "Tenant Added!",
          description: "New tenant has been added successfully"
        });
      }

      onSuccess();
      onClose();
      resetForm();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to add tenant",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  function resetForm() {
    setFormData({
      property_id: '',
      room_id: '',
      name: '',
      phone: '',
      email: '',
      kyc_type: '',
      kyc_number: '',
      check_in_date: new Date().toISOString().split('T')[0],
      rent_amount: '',
      deposit_amount: '',
      emergency_contact: ''
    });
    setSelectedRoomInfo(null);
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{bookingId ? 'Move-In: Review Tenant Details' : 'Add New Tenant'}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Property *</Label>
              <select
                value={formData.property_id}
                onChange={(e) => setFormData({...formData, property_id: e.target.value, room_id: ''})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">Select Property</option>
                {properties.map(prop => (
                  <option key={prop.id} value={prop.id}>{prop.name}</option>
                ))}
              </select>
            </div>

            <div>
              <Label>Room *</Label>
              <select
                value={formData.room_id}
                onChange={(e) => handleRoomChange(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
                disabled={!formData.property_id}
              >
                <option value="">Select Room</option>
                {rooms.map(room => {
                  const occupiedBeds = room.occupied_beds || 0;
                  const totalBeds = room.total_beds || 1;
                  const isFull = totalBeds > 1 ? occupiedBeds >= totalBeds : occupiedBeds >= 2;
                  
                  return (
                    <option key={room.id} value={room.id} disabled={isFull}>
                      Room {room.room_number}
                      {room.room_type && ` - ${room.room_type}`}
                      {` (${occupiedBeds}/${totalBeds} beds)`}
                      {isFull && ' - FULL'}
                    </option>
                  );
                })}
              </select>
            </div>

            <div>
              <Label>Tenant Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="John Doe"
                required
              />
            </div>

            <div>
              <Label>Phone Number *</Label>
              <Input
                type="tel"
                value={formData.phone}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                  setFormData({...formData, phone: value});
                }}
                placeholder="10-digit phone number"
                maxLength={10}
                required
              />
              {formData.phone && formData.phone.length !== 10 && (
                <p className="text-xs text-red-600 mt-1">Phone number must be exactly 10 digits</p>
              )}
            </div>

            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                placeholder="john@example.com"
              />
            </div>

            <div>
              <Label>Check-in Date *</Label>
              <Input
                type="date"
                value={formData.check_in_date}
                onChange={(e) => setFormData({...formData, check_in_date: e.target.value})}
                required
              />
            </div>

            <div>
              <Label>KYC Document Type</Label>
              <select
                value={formData.kyc_type}
                onChange={(e) => setFormData({...formData, kyc_type: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select Document Type</option>
                <option value="pan_card">PAN Card</option>
                <option value="aadhar_card">Aadhar Card</option>
                <option value="driving_license">Driving License</option>
              </select>
            </div>

            <div>
              <Label>KYC Document Number</Label>
              <Input
                value={formData.kyc_number}
                onChange={(e) => {
                  let value = e.target.value;
                  if (formData.kyc_type === 'aadhar_card') {
                    value = value.replace(/\D/g, '').slice(0, 12);
                  } else if (formData.kyc_type === 'pan_card') {
                    value = value.toUpperCase().slice(0, 10);
                  }
                  setFormData({...formData, kyc_number: value});
                }}
                placeholder={
                  formData.kyc_type === 'aadhar_card' ? '12-digit Aadhar number' :
                  formData.kyc_type === 'pan_card' ? '10-character PAN (e.g., ABCDE1234F)' :
                  'Enter document number'
                }
                maxLength={formData.kyc_type === 'aadhar_card' ? 12 : formData.kyc_type === 'pan_card' ? 10 : undefined}
              />
              {formData.kyc_type === 'aadhar_card' && formData.kyc_number && formData.kyc_number.length !== 12 && (
                <p className="text-xs text-red-600 mt-1">Aadhar number must be exactly 12 digits</p>
              )}
              {formData.kyc_type === 'pan_card' && formData.kyc_number && formData.kyc_number.length !== 10 && (
                <p className="text-xs text-red-600 mt-1">PAN must be exactly 10 characters</p>
              )}
            </div>

            {/* Show existing tenants in selected room */}
            {selectedRoomInfo && selectedRoomInfo.tenants && selectedRoomInfo.tenants.length > 0 && (
              <div className="col-span-2 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm font-semibold text-blue-900 mb-2">
                  Existing Tenant{selectedRoomInfo.tenants.length > 1 ? 's' : ''} in Room {selectedRoomInfo.room_number}:
                </p>
                {selectedRoomInfo.tenants.map((tenant) => (
                  <div key={tenant.id || tenant.phone || tenant.name} className="text-sm text-blue-800 flex justify-between items-center mb-1">
                    <span>• {tenant.name}</span>
                    <span className="font-medium">Rent: ₹{tenant.rent_amount || 0}/month</span>
                  </div>
                ))}
                {selectedRoomInfo.total_beds === 1 ? (
                  <p className="text-xs text-blue-600 mt-2">
                    💡 <strong>Shared Room:</strong> Rent auto-filled to ₹0 for 2nd tenant. Change if needed.
                  </p>
                ) : (
                  <p className="text-xs text-blue-600 mt-2">
                    💡 Tip: Set rent amount for the new tenant considering room sharing
                  </p>
                )}
              </div>
            )}

            <div>
              <Label>Monthly Rent *</Label>
              <Input
                type="number"
                value={formData.rent_amount}
                onChange={(e) => setFormData({...formData, rent_amount: e.target.value})}
                placeholder="5000"
                required
              />
              {selectedRoomInfo && selectedRoomInfo.total_beds === 1 && selectedRoomInfo.tenants?.length > 0 && formData.rent_amount === '0' && (
                <p className="text-xs text-green-600 mt-1">✓ Auto-filled for shared room (editable)</p>
              )}
            </div>

            <div>
              <Label>Security Deposit</Label>
              <Input
                type="number"
                value={formData.deposit_amount}
                onChange={(e) => setFormData({...formData, deposit_amount: e.target.value})}
                placeholder="10000 (optional for 2nd tenant)"
              />
              <p className="text-xs text-gray-500 mt-1">Optional: Leave blank or enter 0 if not applicable</p>
            </div>

            {/* Token adjustment — shown only on Move-In (booking has a paid token) */}
            {bookingId && formData.token_paid && (formData.token_amount > 0) && (
              <div className="col-span-2 p-3 bg-purple-50 border border-purple-200 rounded-lg" data-testid="token-adjust-block">
                <p className="text-sm font-semibold text-purple-900 mb-2">
                  💰 Token of ₹{Number(formData.token_amount).toLocaleString('en-IN')} was paid at booking — where should it be adjusted?
                </p>
                <div className="flex gap-3">
                  <label className="flex items-center gap-2 cursor-pointer p-2 rounded border flex-1 hover:bg-white" style={{ borderColor: formData.token_adjust_to === 'deposit' ? '#7e22ce' : '#e5e7eb' }}>
                    <input
                      type="radio"
                      name="token_adjust_to"
                      value="deposit"
                      checked={formData.token_adjust_to === 'deposit'}
                      onChange={(e) => setFormData({...formData, token_adjust_to: e.target.value})}
                      className="accent-purple-600"
                      data-testid="token-adjust-deposit"
                    />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Security Deposit</p>
                      <p className="text-[10px] text-gray-500">Credits the token towards the deposit collected.</p>
                    </div>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer p-2 rounded border flex-1 hover:bg-white" style={{ borderColor: formData.token_adjust_to === 'rent' ? '#7e22ce' : '#e5e7eb' }}>
                    <input
                      type="radio"
                      name="token_adjust_to"
                      value="rent"
                      checked={formData.token_adjust_to === 'rent'}
                      onChange={(e) => setFormData({...formData, token_adjust_to: e.target.value})}
                      className="accent-purple-600"
                      data-testid="token-adjust-rent"
                    />
                    <div>
                      <p className="text-sm font-medium text-gray-900">First Month's Rent</p>
                      <p className="text-[10px] text-gray-500">Marks rent paid if token covers full month.</p>
                    </div>
                  </label>
                </div>
              </div>
            )}

            <div>
              <Label>Emergency Contact Name</Label>
              <Input
                value={formData.emergency_contact_name}
                onChange={(e) => setFormData({...formData, emergency_contact_name: e.target.value})}
                placeholder="Parent/Guardian name"
              />
            </div>

            <div>
              <Label>Emergency Contact Number</Label>
              <Input
                type="tel"
                value={formData.emergency_contact_number}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                  setFormData({...formData, emergency_contact_number: value});
                }}
                placeholder="10-digit phone number"
                maxLength={10}
              />
              {formData.emergency_contact_number && formData.emergency_contact_number.length !== 10 && (
                <p className="text-xs text-red-600 mt-1">Phone number must be exactly 10 digits</p>
              )}
            </div>
          </div>

          <div className="flex gap-4 pt-4">
            <Button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700" disabled={loading}>
              {loading ? (bookingId ? 'Moving In...' : 'Adding...') : (bookingId ? 'Confirm Move-In' : 'Add Tenant')}
            </Button>
            <Button type="button" onClick={onClose} variant="outline" className="flex-1">
              Cancel
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddTenantModal;
